// Create/edit form for one product row (not pricing — see PriceTierPanel.jsx
// for that). Used inline on CatalogPage, same "expand a panel near the
// trigger, no full-screen modal" language as ProductPicker.jsx's own search
// panel — this app has no Modal component and this task doesn't need one.
//
// SKU is a purely server-generated, display-only concept now — there is
// nothing to type and nothing to show in CREATE mode (catalogService.
// createProduct allocates it, format `PRD-000001`, inside the same
// transaction as the INSERT; see routes/adminCatalogRoutes.js's
// productCreateSchema, which has no `sku` field at all). This form never
// submits `sku` in a create body. EDIT mode still shows it, read-only/
// disabled, once the product (and its SKU) actually exists.
//
// No cost/margin field anywhere in this form — a product's cost lives on
// the separate Cost management screen (adminCatalogRoutes.js's own
// PUT /admin/catalog/products/:id/cost, not touched by this component at
// all). GET /admin/catalog/products/:id (fetched below for edit mode, to
// prefill attribute values) also carries a `cost` object — deliberately
// never destructured or read anywhere in this component.
//
// Attribute fields (catalog-restructure follow-up): driven entirely by
// GET /admin/catalog/categories/:id/effective-attributes for the currently
// selected category — see useEffectiveAttributes(). Values for ALL
// attribute keys ever entered are kept in one `attributeValues` state
// object for the lifetime of this form instance; switching category never
// deletes anything from it. Instead, both rendering and submission filter
// through the CURRENT category's effective-attribute list, so a key that
// isn't part of the current category's set is simply neither shown nor
// submitted — and a key shared between the old and new category (e.g.
// `ram`/`storage_type`, inherited by every Computers child) keeps its value
// automatically, without any extra reconciliation code. This satisfies this
// task's own brief ("don't silently carry over screen_size after switching
// from Laptops to Servers... but DO keep ram/storage_type") as a natural
// consequence of filtering-by-current-set rather than as a separate rule.
//
// Every attribute is optional client-side, even one with `required: true` —
// this project's rule is that a product must never be blocked by an
// unfilled attribute (root CLAUDE.md's empty-state philosophy extended to
// this form by this task's own brief). `required` only adds a visual
// asterisk, never a validation gate.

import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useAdminProductDetail,
  useEffectiveAttributes,
  useUploadProductImage,
  useProductBrands,
  useCreateProductBrand,
} from '../hooks/useAdminCatalog.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import { Spinner } from 'shared/components/Feedback/index.js';
import styles from './ProductForm.module.css';

// The <select>'s trailing "+ Add new brand…" option value — picking it
// reveals the inline add-brand mini-form in place of the select itself (this
// form's existing "expand near the trigger, no modal" language, same as the
// photo section below). Namespaced/unlikely-to-collide with a real brand
// name on purpose.
const ADD_NEW_BRAND_VALUE = '__add_new__';

const UNIT_DEFAULT = 'pcs';
const CONDITION_DEFAULT = 'new';
const CONDITION_OPTIONS = ['new', 'refurbished_manufacturer', 'refurbished_inhouse', 'used'];

// Mirrors the server's own limits (handleProductImageUpload in
// routes/adminCatalogRoutes.js) — fast client-side feedback only, the
// server re-validates regardless, same relationship every other client
// validate() in this app already has to its server-side check.
const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

// 'refurbished_manufacturer' -> 'refurbishedManufacturer', matching this
// file's existing catalog.condition.* key-naming convention (camelCase
// segments, not snake_case — shared/i18n/strings.js's own comment on that
// block).
function conditionKeySegment(condition) {
  return condition.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function initialState(product) {
  return {
    sku: product?.sku ?? '',
    name: product?.name ?? '',
    nameAm: product?.nameAm ?? '',
    brand: product?.brand ?? '',
    model: product?.model ?? '',
    spec: product?.spec ?? '',
    unit: product?.unit ?? UNIT_DEFAULT,
    categoryId: product?.categoryId != null ? String(product.categoryId) : '',
    stockQty: product?.stockQty != null ? String(product.stockQty) : '',
    condition: product?.condition ?? CONDITION_DEFAULT,
    displayDescription: product?.displayDescription ?? '',
  };
}

export default function ProductForm({ mode, product, categories, onSubmit, onCancel, isPending }) {
  const { t, lang } = useLanguage();
  const { toastOk, toastErr } = useActionFeedback();
  const [values, setValues] = useState(() => initialState(product));
  const [clientErrorId, setClientErrorId] = useState(null);

  const isCreate = mode === 'create';
  const set = (field) => (e) => setValues((prev) => ({ ...prev, [field]: e.target.value }));

  // Edit-mode-only detail read, to prefill attribute values the list row
  // never carries (see hooks/useAdminCatalog.js's own comment on why the
  // list read stays flat/cheap).
  const detailQuery = useAdminProductDetail(!isCreate ? product?.id : undefined);

  const [attributeValues, setAttributeValues] = useState({});
  const attributesInitializedRef = useRef(false);
  useEffect(() => {
    if (!isCreate && detailQuery.data && !attributesInitializedRef.current) {
      setAttributeValues(detailQuery.data.attributes || {});
      attributesInitializedRef.current = true;
    }
  }, [isCreate, detailQuery.data]);

  const setAttribute = (key) => (value) =>
    setAttributeValues((prev) => ({ ...prev, [key]: value }));

  const categoryId = values.categoryId ? Number(values.categoryId) : undefined;
  const effectiveAttributesQuery = useEffectiveAttributes(categoryId);
  const effectiveAttributes = categoryId ? effectiveAttributesQuery.data || [] : [];
  // Gate rendering on both reads being ready (in edit mode) so attribute
  // fields never flash empty before their real prefilled values arrive —
  // simpler than a dedicated skeleton for a sub-section of an already-small
  // form.
  const attributesReady = !categoryId || Boolean(effectiveAttributesQuery.data);
  const detailReady = isCreate || Boolean(detailQuery.data);

  // Brand field: a managed dropdown (GET /admin/catalog/brands, active
  // only) with an inline "add new brand" mini-form, replacing the old
  // free-text input + <datalist> suggestions. `values.brand` stays a plain
  // string either way — products.brand has no FK (catalogService's own
  // comment on product_brands) — this UI only keeps the typed values
  // consistent.
  const brandsQuery = useProductBrands();
  const brandOptions = brandsQuery.data || [];
  // Defensive: a product being edited may carry a `brand` value that
  // predates this managed list (a legacy typed value) — or, before the
  // inline add below actually succeeds, the just-submitted name. Either
  // way, the select must keep showing/selecting it rather than silently
  // blanking the field, same "value not in options" precaution any
  // controlled select needs.
  const brandOptionsWithCurrent =
    values.brand && !brandOptions.some((b) => b.name === values.brand)
      ? [...brandOptions, { id: `current-${values.brand}`, name: values.brand }]
      : brandOptions;

  const createBrandMutation = useCreateProductBrand();
  const [addingBrand, setAddingBrand] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [brandErrorId, setBrandErrorId] = useState(null);

  const handleBrandSelectChange = (e) => {
    const next = e.target.value;
    if (next === ADD_NEW_BRAND_VALUE) {
      setBrandErrorId(null);
      setAddingBrand(true);
      return;
    }
    setValues((prev) => ({ ...prev, brand: next }));
  };

  const handleBrandAddCancel = () => {
    setAddingBrand(false);
    setNewBrandName('');
    setBrandErrorId(null);
  };

  const handleBrandAddSubmit = (e) => {
    e.preventDefault();
    setBrandErrorId(null);
    if (!newBrandName.trim()) {
      setBrandErrorId('catalog.form.brandNewRequired');
      return;
    }
    createBrandMutation.mutate(
      { name: newBrandName.trim() },
      {
        onSuccess: (data) => {
          setValues((prev) => ({ ...prev, brand: data.brand.name }));
          setAddingBrand(false);
          setNewBrandName('');
        },
        onError: (err) => toastErr(err, ['DUPLICATE_BRAND_NAME', 'VALIDATION_ERROR']),
      }
    );
  };

  // Photo section (edit mode only — a brand-new product has no id yet to
  // upload against, see this component's header comment). `cacheBust` is
  // appended as a `?t=` query string only after a successful upload in THIS
  // session; the filename productImageStorage.js writes is deterministic
  // per product id (e.g. product-818-thumb.webp), so without this a browser
  // could keep showing the old cached photo after a replacement.
  const uploadImageMutation = useUploadProductImage();
  const [imageFile, setImageFile] = useState(null);
  const [photoErrorId, setPhotoErrorId] = useState(null);
  const [cacheBust, setCacheBust] = useState(0);
  const fileInputRef = useRef(null);

  const currentImageUrl = !isCreate
    ? detailQuery.data?.image_url || detailQuery.data?.brand_image_url
    : null;
  const previewSrc = currentImageUrl
    ? `${currentImageUrl}${cacheBust ? `${currentImageUrl.includes('?') ? '&' : '?'}t=${cacheBust}` : ''}`
    : null;

  const handleImageFileChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setPhotoErrorId(null);
    if (!file) {
      setImageFile(null);
      return;
    }
    if (!ACCEPTED_IMAGE_TYPES.has(file.type)) {
      setPhotoErrorId('catalog.form.photoInvalidType');
      setImageFile(null);
      e.target.value = '';
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setPhotoErrorId('catalog.form.photoTooLarge');
      setImageFile(null);
      e.target.value = '';
      return;
    }
    setImageFile(file);
  };

  const handleImageUpload = () => {
    if (!imageFile || !product?.id) return;
    setPhotoErrorId(null);
    uploadImageMutation.mutate(
      { id: product.id, file: imageFile },
      {
        onSuccess: () => {
          setImageFile(null);
          setCacheBust(Date.now());
          if (fileInputRef.current) fileInputRef.current.value = '';
          toastOk(t('catalog.form.photoUploadedToast'));
        },
        onError: (err) => toastErr(err, ['VALIDATION_ERROR', 'PRODUCT_NOT_FOUND']),
      }
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setClientErrorId(null);

    if (!values.name.trim()) {
      setClientErrorId('catalog.form.nameRequired');
      return;
    }

    const attributesBody = {};
    for (const def of effectiveAttributes) {
      const raw = attributeValues[def.attribute_key];
      if (def.input_type === 'boolean') {
        attributesBody[def.attribute_key] = raw === true || raw === 'true';
      } else if (typeof raw === 'string' && raw.trim() !== '') {
        attributesBody[def.attribute_key] = raw.trim();
      }
    }

    const body = {
      name: values.name.trim(),
      nameAm: values.nameAm.trim() || undefined,
      brand: values.brand.trim() || undefined,
      model: values.model.trim() || undefined,
      spec: values.spec.trim() || undefined,
      unit: values.unit.trim() || UNIT_DEFAULT,
      categoryId: values.categoryId ? Number(values.categoryId) : undefined,
      stockQty: values.stockQty !== '' ? Number(values.stockQty) : undefined,
      condition: values.condition,
      attributes: attributesBody,
      displayDescription: values.displayDescription.trim() || undefined,
    };

    onSubmit(body);
  };

  const shownErrorId = clientErrorId;

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <h2 className={styles.title}>
        {t(isCreate ? 'catalog.form.createTitle' : 'catalog.form.editTitle')}
      </h2>

      <div className={styles.grid}>
        {!isCreate && (
          <label className={styles.field}>
            {t('catalog.form.skuLabel')}
            <input
              type="text"
              value={values.sku}
              disabled
              className={styles.input}
              title={t('catalog.form.skuImmutableHint')}
            />
          </label>
        )}

        <label className={styles.field}>
          {t('catalog.form.nameLabel')}
          <input
            type="text"
            value={values.name}
            onChange={set('name')}
            autoFocus
            className={styles.input}
          />
        </label>

        <label className={styles.field}>
          {t('catalog.form.nameAmLabel')}
          <input
            type="text"
            value={values.nameAm}
            onChange={set('nameAm')}
            className={styles.input}
          />
        </label>

        {addingBrand ? (
          <div className={styles.field}>
            {t('catalog.form.brandNewLabel')}
            <div className={styles.brandAddRow}>
              <input
                type="text"
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
                autoFocus
                maxLength={80}
                className={styles.input}
              />
              <button
                type="button"
                className={styles.brandAddButton}
                onClick={handleBrandAddSubmit}
                disabled={createBrandMutation.isPending}
              >
                {createBrandMutation.isPending && <Spinner />}{' '}
                {createBrandMutation.isPending
                  ? t('catalog.form.brandAdding')
                  : t('catalog.form.brandAdd')}
              </button>
              <button
                type="button"
                className={styles.brandCancelButton}
                onClick={handleBrandAddCancel}
              >
                {t('catalog.form.cancel')}
              </button>
            </div>
            {brandErrorId && <span className={styles.error}>{t(brandErrorId)}</span>}
          </div>
        ) : (
          <label className={styles.field}>
            {t('catalog.form.brandLabel')}
            <select
              value={values.brand}
              onChange={handleBrandSelectChange}
              className={styles.input}
            >
              <option value="">{t('catalog.form.brandNoneOption')}</option>
              {brandOptionsWithCurrent.map((b) => (
                <option key={b.id} value={b.name}>
                  {b.name}
                </option>
              ))}
              <option value={ADD_NEW_BRAND_VALUE}>{t('catalog.form.brandAddNewOption')}</option>
            </select>
          </label>
        )}

        <label className={styles.field}>
          {t('catalog.form.modelLabel')}
          <input
            type="text"
            value={values.model}
            onChange={set('model')}
            className={styles.input}
          />
        </label>

        <label className={styles.field}>
          {t('catalog.form.unitLabel')}
          <input type="text" value={values.unit} onChange={set('unit')} className={styles.input} />
        </label>

        <label className={styles.field}>
          {t('catalog.form.categoryLabel')}
          <select value={values.categoryId} onChange={set('categoryId')} className={styles.input}>
            <option value="">{t('catalog.form.categoryNoneOption')}</option>
            {(categories || []).map((cat) => (
              <option key={cat.id} value={cat.id}>
                {lang === 'am' && cat.name_am ? cat.name_am : cat.name}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.field}>
          {t('catalog.form.stockQtyLabel')}
          <input
            type="number"
            min="0"
            step="1"
            value={values.stockQty}
            onChange={set('stockQty')}
            className={`${styles.input} tabularNums`}
          />
        </label>

        <label className={styles.field}>
          {t('catalog.form.conditionLabel')}
          <select value={values.condition} onChange={set('condition')} className={styles.input}>
            {CONDITION_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {t(`catalog.condition.${conditionKeySegment(option)}`)}
              </option>
            ))}
          </select>
        </label>

        <label className={`${styles.field} ${styles.fieldWide}`}>
          {t('catalog.form.specLabel')}
          <textarea
            value={values.spec}
            onChange={set('spec')}
            rows={2}
            className={styles.textarea}
          />
        </label>

        <label className={`${styles.field} ${styles.fieldWide}`}>
          {t('catalog.form.displayDescriptionLabel')}
          <textarea
            value={values.displayDescription}
            onChange={set('displayDescription')}
            rows={2}
            className={styles.textarea}
          />
          <span className={styles.hint}>{t('catalog.form.displayDescriptionHint')}</span>
        </label>
      </div>

      {/* Photo section: edit mode only — a brand-new product being created
          has no id yet to upload against (this component's header
          comment). Gated on detailReady the same way the attributes
          section below is, so the preview never flashes empty before the
          real image_url arrives; a skeleton fills the reserved preview
          area while that read is in flight instead. */}
      {!isCreate && (
        <div className={styles.photoSection}>
          <h3 className={styles.attributesTitle}>{t('catalog.form.photoSectionTitle')}</h3>
          <div className={styles.photoRow}>
            {!detailReady ? (
              <div className={`${styles.photoPreview} skeleton`} />
            ) : previewSrc ? (
              <img
                src={previewSrc}
                alt={t('catalog.form.photoAltText')}
                className={styles.photoPreview}
              />
            ) : (
              <div className={styles.photoPreview}>
                <span className={styles.photoPlaceholderText}>{t('catalog.form.photoNone')}</span>
              </div>
            )}

            <div className={styles.photoControls}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageFileChange}
                className={styles.photoFileInput}
              />
              <span className={styles.hint}>{t('catalog.form.photoHint')}</span>
              <button
                type="button"
                className={styles.photoUploadButton}
                onClick={handleImageUpload}
                disabled={!imageFile || uploadImageMutation.isPending}
              >
                {uploadImageMutation.isPending && <Spinner />}{' '}
                {uploadImageMutation.isPending
                  ? t('catalog.form.photoUploading')
                  : t('catalog.form.photoUpload')}
              </button>
              {photoErrorId && <p className={styles.error}>{t(photoErrorId)}</p>}
            </div>
          </div>
        </div>
      )}

      {attributesReady && detailReady && effectiveAttributes.length > 0 && (
        <div className={styles.attributesSection}>
          <h3 className={styles.attributesTitle}>{t('catalog.form.attributesSectionTitle')}</h3>
          <div className={styles.grid}>
            {effectiveAttributes.map((def) => {
              const label = lang === 'am' && def.label_am ? def.label_am : def.label;
              const raw = attributeValues[def.attribute_key];

              if (def.input_type === 'boolean') {
                const checked = raw === true || raw === 'true';
                return (
                  <label key={def.attribute_key} className={styles.checkboxField}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => setAttribute(def.attribute_key)(e.target.checked)}
                    />
                    {label}
                    {Boolean(def.required) && <span className={styles.requiredMark}> *</span>}
                  </label>
                );
              }

              if (def.input_type === 'select') {
                return (
                  <label key={def.attribute_key} className={styles.field}>
                    {label}
                    {Boolean(def.required) && <span className={styles.requiredMark}> *</span>}
                    <select
                      value={raw ?? ''}
                      onChange={(e) => setAttribute(def.attribute_key)(e.target.value)}
                      className={styles.input}
                    >
                      <option value="">{t('catalog.form.attributeUnsetOption')}</option>
                      {(def.options || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {lang === 'am' && opt.labelAm ? opt.labelAm : opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              }

              return (
                <label key={def.attribute_key} className={styles.field}>
                  {label}
                  {Boolean(def.required) && <span className={styles.requiredMark}> *</span>}
                  <input
                    type={def.input_type === 'number' ? 'number' : 'text'}
                    value={raw ?? ''}
                    onChange={(e) => setAttribute(def.attribute_key)(e.target.value)}
                    className={styles.input}
                  />
                </label>
              );
            })}
          </div>
        </div>
      )}

      {shownErrorId && <p className={styles.error}>{t(shownErrorId)}</p>}

      <div className={styles.actions}>
        <button type="button" className={styles.cancelButton} onClick={onCancel}>
          {t('catalog.form.cancel')}
        </button>
        <button type="submit" className={styles.submitButton} disabled={isPending}>
          {isPending && <Spinner />}{' '}
          {isCreate
            ? isPending
              ? t('catalog.form.submitting')
              : t('catalog.form.submitCreate')
            : isPending
              ? t('catalog.form.saving')
              : t('catalog.form.submitSave')}
        </button>
      </div>
    </form>
  );
}
